import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DialCheckStage from "./DialCheckStage.jsx";
import FunnelStage from "./FunnelStage.jsx";
import RoomStage from "./RoomStage.jsx";
import DebriefStage from "./DebriefStage.jsx";
import "./SignalBoost.css";

const STAGES = ["intro", "dial", "funnel", "room", "debrief"];
const STAGE_LABELS = { dial: "Dial Check", funnel: "Funnel", room: "The Room", debrief: "Broadcast" };

export default function SignalBoost() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("intro");
  const [dialResult, setDialResult] = useState(null);
  const [funnelResult, setFunnelResult] = useState(null);
  const [roomResult, setRoomResult] = useState(null);

  const restart = () => {
    setDialResult(null);
    setFunnelResult(null);
    setRoomResult(null);
    setStage("intro");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="panel relative overflow-hidden p-6">
        <div className="mb-6">
          <p className="label-pixel text-magenta">Level 09</p>
          <h1 className="pixel-heading text-base text-mist sm:text-xl">Signal Boost</h1>
        </div>

        {stage !== "intro" && (
          <div className="mb-6 flex gap-1.5">
            {["dial", "funnel", "room", "debrief"].map((s) => {
              const idx = STAGES.indexOf(s);
              const currentIdx = STAGES.indexOf(stage);
              const complete = idx < currentIdx;
              const active = s === stage;
              return (
                <div key={s} className="flex-1">
                  <div
                    className={`h-1.5 w-full border border-black/60 ${
                      complete ? "bg-lime" : active ? "bg-cyan" : "bg-panel2"
                    }`}
                  />
                  <p
                    className={`mt-1 text-center font-display text-[7px] uppercase ${
                      active ? "text-cyan" : complete ? "text-lime" : "text-mist/30"
                    }`}
                  >
                    {STAGE_LABELS[s]}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="sb-panel-transition" key={stage}>
          {stage === "intro" && (
            <div className="text-center">
              <p className="mb-3 text-sm text-mist/80">
                Your voice is an instrument with <span className="text-cyan">88 keys</span>, and
                most people play the same three their entire life. Listeners decide how put-
                together you look on <span className="text-magenta">sight</span> — but they decide
                whether to actually <span className="text-magenta">believe</span> you the moment
                you open your mouth.
              </p>
              <p className="mb-6 text-xs text-mist/50">
                Three phases: tune the five dials that make a voice sound alive, learn the
                frameworks that turn rambling into a distilled point, then step into The Room —
                negotiations, hot-seat questions, and live calls where all of it gets tested at
                once.
              </p>
              <button onClick={() => setStage("dial")} className="btn-magenta">
                ▶ GO LIVE
              </button>
            </div>
          )}

          {stage === "dial" && (
            <DialCheckStage
              onComplete={(result) => {
                setDialResult(result);
                setStage("funnel");
              }}
            />
          )}

          {stage === "funnel" && (
            <FunnelStage
              onComplete={(result) => {
                setFunnelResult(result);
                setStage("room");
              }}
            />
          )}

          {stage === "room" && dialResult && funnelResult && (
            <RoomStage
              onComplete={(result) => {
                setRoomResult(result);
                setStage("debrief");
              }}
            />
          )}

          {stage === "debrief" && dialResult && funnelResult && roomResult && (
            <DebriefStage
              dialResult={dialResult}
              funnelResult={funnelResult}
              roomResult={roomResult}
              onRetry={restart}
            />
          )}
        </div>
      </div>
    </div>
  );
}
